import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ApiResponseVmBackups } from '../types/api/ApiResponseBackup';
import type { BackupSummary, BackupVmRef, BackupVmTotals } from '../types/models/Backup';

/**
 * `GET /datacenters/vms/{id}/backups` — the backup subjects matching one VM. Unpaginated by
 * contract; a VM has at most a handful.
 *
 * The join key is the **VM name**, matched exactly and case-sensitively against the level-4
 * directory name — not a stored NetBox id. Two consequences the UI must be honest about:
 *
 * - A **retired** directory keeps its `.old` suffix, so it never matches a live NetBox VM and
 *   never appears here. Those are reachable from the Backups page instead.
 * - Several rows can match one VM (two hypervisors, or a mirrored target), hence an array.
 *
 * An empty array is a success, not an error. No-ops while `vmId` is `null`.
 *
 * `totals` is the server-side storage aggregate, **not** a sum of `backups`: this hook always
 * requests `include_missing=1` (so a vanished directory stays visible in the list), while the
 * aggregate deliberately excludes missing subjects. It stays `null` until a successful load — and
 * on a backend too old to publish the key — so a consumer can tell "no footprint reported" apart
 * from a genuine zero.
 */
export function useVmBackups(vmId: number | null) {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [backups, setBackups] = useState<BackupSummary[]>([]);
  const [count, setCount] = useState(0);
  const [totals, setTotals] = useState<BackupVmTotals | null>(null);
  const [vm, setVm] = useState<BackupVmRef | null>(null);
  const [loading, setLoading] = useState(vmId !== null);
  const [error, setError] = useState<Error | null>(null);

  const fetchVmBackups = useCallback(async () => {
    if (vmId === null || !auth.user?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/datacenters/vms/${vmId}/backups?include_missing=1`);
      const json = (await response.json().catch(() => null)) as ApiResponseVmBackups | null;

      if (json === null) {
        throw new Error(`Unexpected response from server (HTTP ${response.status})`);
      }
      if (json.status !== 'success' || !json.data) {
        throw new Error(json.message ?? 'Unexpected response from server');
      }

      setBackups(json.data.backups ?? []);
      setCount(json.data.count ?? 0);
      setTotals(json.data.totals ?? null);
      setVm(json.data.vm ?? null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      setLoading(false);
    }
  }, [vmId, auth.user?.access_token, apiFetch]);

  useEffect(() => {
    fetchVmBackups();
  }, [fetchVmBackups]);

  return { backups, count, totals, vm, loading, error, refetch: fetchVmBackups };
}
